import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Star } from "lucide-react";

/**
 * SocialProof — displays testimonials, average rating, member count.
 *
 * IMPORTANT: rends STRICTEMENT rien tant qu'il n'y a aucun vrai témoignage.
 * Aucun faux avis, aucun compteur mensonger, aucun signal de rareté artificiel.
 * Le composant s'activera automatiquement dès que /api/testimonials renverra
 * au moins un élément.
 */
export function SocialProof() {
  const [testimonials, setTestimonials] = useState(null); // null = loading, [] = none, [...] = data

  useEffect(() => {
    api.get("/testimonials")
      .then((r) => setTestimonials(r.data.data || []))
      .catch(() => setTestimonials([]));
  }, []);

  // Nothing to prove yet — render nothing to avoid empty/embarrassing states.
  if (!testimonials || testimonials.length === 0) return null;

  const withRating = testimonials.filter((t) => typeof t.rating === "number");
  const avgRating = withRating.length
    ? (withRating.reduce((s, t) => s + t.rating, 0) / withRating.length).toFixed(1)
    : null;

  return (
    <section className="bg-[#F3F6F1] py-24" data-testid="social-proof-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="mb-12 flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Ils ont franchi le pas</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40] max-w-2xl">
              Les retours des élèves.
            </h2>
          </div>
          {avgRating && (
            <div className="flex items-center gap-3 bg-white rounded-2xl px-5 py-3 border-2 border-[#1A2A40]/10" data-testid="avg-rating">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < Math.round(avgRating) ? "fill-[#FFD166] text-[#FFD166]" : "text-[#1A2A40]/20"}`} />
                ))}
              </div>
              <div className="font-bold text-[#1A2A40]">
                {avgRating}<span className="text-sm text-[#4A5568]"> / 5 · {testimonials.length} avis</span>
              </div>
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-[#FDFBF7] rounded-3xl p-8 border-2 border-[#1A2A40]/10" data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFD166] flex items-center justify-center text-2xl shadow-brut-sm">
                  {t.avatar || "🎌"}
                </div>
                <div>
                  <div className="font-bold text-[#1A2A40]">{t.name}</div>
                  {t.level && (
                    <div className="text-xs text-[#E94A3F] uppercase tracking-wider font-bold">{t.level}</div>
                  )}
                </div>
              </div>
              <p className="text-[#4A5568] leading-relaxed">« {t.quote} »</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default SocialProof;
